/**
 * Redirect sanitization utilities
 *
 * Prevents open redirect vulnerabilities by validating redirect targets
 * before using them in navigation.
 */

import { API_ORIGIN } from "@/api/config";

/**
 * Options for sanitizeRedirect
 */
interface SanitizeRedirectOptions {
  /**
   * When true, allows redirects to the API origin (for extension OAuth flow).
   * Only the API origin is allowed, not arbitrary external URLs.
   */
  allowApiOrigin?: boolean;
}

/**
 * Sanitizes a redirect target to prevent open redirect attacks.
 *
 * Only allows relative URLs that start with "/" and rejects:
 * - Absolute URLs (http://, https://, //)
 * - JavaScript URLs (javascript:)
 * - Data URLs (data:)
 * - Any other protocol
 *
 * When options.allowApiOrigin is true, also allows redirects to the
 * API origin (used for extension OAuth callback flow).
 *
 * @param rawNext - The unsanitized redirect target from user input
 * @param options - Optional configuration
 * @returns A safe redirect path/URL, or "/" if the input is invalid
 */
export function sanitizeRedirect(
  rawNext: string | null | undefined,
  options: SanitizeRedirectOptions = {}
): string {
  const trimmed = (rawNext || "").trim();
  if (!trimmed) {
    return "/";
  }

  try {
    // Parse as URL with localhost as base to detect absolute URLs
    const url = new URL(trimmed, "http://localhost");

    // Check if this is an absolute URL
    if (url.origin !== "http://localhost") {
      // If allowApiOrigin is true, allow redirects to the extension auth callback
      if (options.allowApiOrigin) {
        const absoluteUrl = new URL(trimmed);
        const normalizedTargetOrigin = absoluteUrl.origin.toLowerCase();

        // Get the allowed origin - use API_ORIGIN if set, otherwise use current page origin
        // (In staging/dev, the API is on the same origin as the webapp)
        const allowedOrigin = API_ORIGIN
          ? API_ORIGIN.replace(/\/$/, "").toLowerCase()
          : (typeof window !== "undefined" ? window.location.origin.toLowerCase() : "");

        if (normalizedTargetOrigin === allowedOrigin) {
          // Only allow the extension auth callback path
          if (absoluteUrl.pathname === "/api/extension/auth/callback") {
            return trimmed;
          }
        }
      }
      // Reject other absolute URLs
      return "/";
    }

    // Reject if pathname doesn't start with /
    if (!url.pathname.startsWith("/")) {
      return "/";
    }

    // Build safe path from pathname, search, and hash
    let path = url.pathname || "/";
    if (url.search) {
      path += url.search;
    }
    if (url.hash) {
      path += url.hash;
    }

    return path || "/";
  } catch {
    // If URL parsing fails, return safe default
    return "/";
  }
}
